import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../app/AuthProvider";
import { useTenants } from "../api/hooks";
import { Button, Card, ErrorNotice, PageHeader } from "../components/ui";
import { IconBuilding } from "../components/icons";
import { tenantRoleLabel } from "../lib/format";

/**
 * Selector de tenant: aparece solo cuando el usuario pertenece a varios o es
 * super-admin (la API marca `requiresTenantSelection`). El token de este paso
 * lleva ya el `tenantId` verificado contra `UserTenant` en la base de datos.
 *
 * Un super-admin de plataforma puede no pertenecer a ningún tenant como tal
 * (no tiene fila `UserTenant`: no es "empleado" de ninguna empresa cliente),
 * así que aquí se combina lo que `login` devuelve (`auth.tenants`, vacío en
 * ese caso) con el listado completo de tenants que solo un super-admin puede
 * pedir (`GET /internal/tenants` → `canCrossTenants`), para poder elegir
 * cualquier empresa sobre la que operar.
 */
export function TenantSelectPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const tenants = useTenants();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const choose = async (tenantId: string): Promise<void> => {
    setPending(tenantId);
    setError(null);
    try {
      await auth.selectTenant(tenantId);
      navigate("/deca", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No se pudo seleccionar el tenant");
    } finally {
      setPending(null);
    }
  };

  const ownTenants = auth.tenants;
  const otherTenants =
    tenants.data?.canCrossTenants
      ? tenants.data.items
          .filter((tenant) => !ownTenants.some((own) => own.tenantId === tenant.id))
          .map((tenant) => ({ tenantId: tenant.id, tenantName: tenant.name, role: null as string | null }))
      : [];
  const options = [...ownTenants, ...otherTenants];

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <PageHeader
        eyebrow="Sesión"
        title="Elige con qué empresa vas a trabajar"
        description="Cada documento, catálogo y reporte pertenece a la empresa seleccionada. Podrás cambiar de empresa desde la barra superior sin volver a entrar."
        actions={
          <Button onClick={auth.logout}>Cerrar sesión</Button>
        }
      />

      {error ? (
        <div className="mb-4">
          <ErrorNotice error={new Error(error)} />
        </div>
      ) : null}

      {options.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-600 dark:text-ink-300">
            Tu usuario no tiene acceso a ninguna empresa todavía. Pide a un administrador que te
            asigne una.
          </p>
        </Card>
      ) : (
        <ul className="space-y-2">
          {options.map((tenant) => (
            <li key={tenant.tenantId}>
              <button
                type="button"
                onClick={() => void choose(tenant.tenantId)}
                disabled={pending !== null}
                className="surface flex w-full items-center gap-4 p-4 text-left hover:border-brand-400 disabled:opacity-60"
              >
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-300">
                  <IconBuilding className="h-5 w-5" />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-ink-900 dark:text-ink-50">
                    {tenant.tenantName}
                  </span>
                  <span className="block text-xs text-ink-500 dark:text-ink-400">
                    {tenant.role ? `Rol: ${tenantRoleLabel(tenant.role)}` : "Operar como super-admin"}
                  </span>
                </span>
                <span className="text-sm text-brand-700 dark:text-brand-300">
                  {pending === tenant.tenantId ? "Abriendo…" : "Entrar →"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
